import { fetchJSON as fetchAPI } from './client';

export type Dependency = { key: string; id: string; name: string; type: string; status: string; description?: string };
export type Objective = { key: string; name: string; category: string; description?: string; mandatory: boolean };
export type TenantHierarchyLevel = { key: string; level: number; name: string; description?: string; status: string };
export type AuthCapability = { key: string; name: string; category: string; description?: string; status: string; future?: boolean };
export type PlatformRole = { key: string; name: string; scope: string; description?: string; builtin: boolean };
export type PermissionAction = { key: string; action: string; description?: string };
export type UserManagementFeature = { key: string; name: string; api_path?: string; status: string };
export type SecurityControl = { key: string; name: string; category: string; description?: string; status: string };
export type SessionFeature = { key: string; name: string; description?: string; status: string };
export type DatabaseTable = { key: string; name: string; service: string; description?: string; has_rls: boolean };
export type BackendAPI = { key: string; method: string; path: string; description?: string; auth_required: boolean };
export type FrontendScreen = { key: string; name: string; route?: string; status: string };
export type ObservabilityEvent = { key: string; event: string; category: string };
export type TestingRequirement = { key: string; name: string; test_type: string; location?: string };
export type QualityGate = { key: string; name: string; command?: string; description?: string; blocking: boolean };
export type OutOfScopeItem = { key: string; name: string; reason?: string };
export type SuccessCriterion = { key: string; criterion: string; validation?: string; mandatory: boolean };

export type SprintDashboard = {
  sprint_id: string; tenant_id: string; version: string; priority: string; applies_to: string[];
  dependencies: number; objectives: number; hierarchy_levels: number; auth_capabilities: number;
  platform_roles: number; permission_actions: number; user_management_features: number;
  security_controls: number; session_features: number; database_tables: number;
  backend_apis: number; frontend_screens: number; observability_events: number;
  testing_requirements: number; quality_gates: number; out_of_scope: number; success_criteria: number;
  generated_at: string;
};

export type SprintValidation = {
  valid: boolean; tenant_isolation_verified: boolean; rbac_verified: boolean;
  authentication_secure: boolean; authorization_enforced: boolean; audit_complete: boolean;
  openapi_generated: boolean; tests_passing: boolean; ci_passing: boolean;
  no_critical_security_findings: boolean; gaps?: string[]; evaluated_at: string;
};

export type Dev101Status = {
  sprint_id: string; version: string; priority: string; platform_roles: number;
  backend_apis: number; database_tables: number; quality_gates: number;
  enterprise_iam: boolean; production_ready: boolean; no_business_logic: boolean; status: string;
};

const GOVERNANCE_BASE = import.meta.env.VITE_GOVERNANCE_API ?? '/api/v1/governance';
const fetchG = <T,>(path: string, init?: RequestInit) => fetchAPI<T>(`${GOVERNANCE_BASE}${path}`, init);

export const enterpriseIdentityPlatformApi = {
  dashboard: () => fetchG<SprintDashboard>('/dev101/dashboard'),
  dependencies: () => fetchG<{ dependencies: Dependency[] }>('/dev101/dependencies').then((r) => r.dependencies),
  objectives: () => fetchG<{ objectives: Objective[] }>('/dev101/objectives').then((r) => r.objectives),
  tenantHierarchy: () => fetchG<{ hierarchy: TenantHierarchyLevel[] }>('/dev101/tenant-hierarchy').then((r) => r.hierarchy),
  authentication: () => fetchG<{ capabilities: AuthCapability[] }>('/dev101/authentication').then((r) => r.capabilities),
  authorization: () => fetchG<{ roles: PlatformRole[] }>('/dev101/authorization').then((r) => r.roles),
  permissionEngine: () => fetchG<{ actions: PermissionAction[] }>('/dev101/permission-engine').then((r) => r.actions),
  userManagement: () => fetchG<{ features: UserManagementFeature[] }>('/dev101/user-management').then((r) => r.features),
  security: () => fetchG<{ controls: SecurityControl[] }>('/dev101/security').then((r) => r.controls),
  sessionManagement: () => fetchG<{ features: SessionFeature[] }>('/dev101/session-management').then((r) => r.features),
  databaseSchema: () => fetchG<{ tables: DatabaseTable[] }>('/dev101/database-schema').then((r) => r.tables),
  backendAPIs: () => fetchG<{ apis: BackendAPI[] }>('/dev101/backend-apis').then((r) => r.apis),
  frontendScreens: () => fetchG<{ screens: FrontendScreen[] }>('/dev101/frontend-screens').then((r) => r.screens),
  observability: () => fetchG<{ events: ObservabilityEvent[] }>('/dev101/observability').then((r) => r.events),
  testing: () => fetchG<{ requirements: TestingRequirement[] }>('/dev101/testing').then((r) => r.requirements),
  qualityGates: () => fetchG<{ gates: QualityGate[] }>('/dev101/quality-gates').then((r) => r.gates),
  outOfScope: () => fetchG<{ items: OutOfScopeItem[] }>('/dev101/out-of-scope').then((r) => r.items),
  successCriteria: () => fetchG<{ criteria: SuccessCriterion[] }>('/dev101/success-criteria').then((r) => r.criteria),
  validate: (attrs: Record<string, boolean>) =>
    fetchG<SprintValidation>('/dev101/validate', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attrs }) }),
  status: () => fetchG<Dev101Status>('/dev101/status'),
};
